/**
 * Centralized registry for mapping validation error codes to visual highlight configurations.
 * Supports theme-aware styling, multi-entity hierarchy, and simulation data overlays.
 */

export type ThemeVariant = 'midnight' | 'foundry' | 'daylight' | 'cyber'
export type PatternType = 'solid' | 'dashed'
export type EntityRole = 'CAUSE' | 'VICTIM'
export type EntityType = 'SWITCH' | 'TRACK_SEGMENT' | 'LOCOMOTIVE' | 'RAIL_CAR' | 'ZONE'

export interface HighlightConfig {
  strokeColor: string;
  strokeWidth: number;
  fillColor?: string;
  glowEnabled: boolean;
  glowColor?: string;
  glowBlur?: number;
  pattern: PatternType;
  labelOverride?: string;
  opacity?: number; // For simulation ghost overlays
}

// Theme color palettes for error states
const themeColors = {
  midnight: {
    electricCrimson: '#FF0055',
    neonCyan: '#00E5FF',
    warningOrange: '#FF9500',
    electricBlue: '#0066FF',
    dangerRed: '#FF0000',
    cautionYellow: '#FFCC00',
  },
  foundry: {
    electricCrimson: '#DC2626',
    neonCyan: '#06B6D4',
    warningOrange: '#EA580C',
    electricBlue: '#2563EB',
    dangerRed: '#B91C1C',
    cautionYellow: '#D97706',
  },
  daylight: {
    electricCrimson: '#DC2626',
    neonCyan: '#0891B2',
    warningOrange: '#EA580C',
    electricBlue: '#2563EB',
    dangerRed: '#B91C1C',
    cautionYellow: '#D97706',
  },
  cyber: {
    electricCrimson: '#FF0055',
    neonCyan: '#00E5FF',
    warningOrange: '#FF9500',
    electricBlue: '#0066FF',
    dangerRed: '#FF0000',
    cautionYellow: '#FFCC00',
  },
}

// Base highlight configurations by error code
const errorConfigurations: Record<string, {
  primaryColor: keyof typeof themeColors.midnight;
  strokeWidth: number;
  glowBlur: number;
  pattern: PatternType;
}> = {
  FOUL_ZONE_VIOLATION: {
    primaryColor: 'electricCrimson',
    strokeWidth: 6,
    glowBlur: 40,
    pattern: 'solid',
  },
  PATH_BLOCKED: {
    primaryColor: 'electricCrimson',
    strokeWidth: 6,
    glowBlur: 40,
    pattern: 'solid',
  },
  HAZMAT_SEGREGATION_VIOLATION: {
    primaryColor: 'neonCyan',
    strokeWidth: 6,
    glowBlur: 40,
    pattern: 'solid',
  },
  CAPACITY_EXCEEDED: {
    primaryColor: 'warningOrange',
    strokeWidth: 6,
    glowBlur: 40,
    pattern: 'solid',
  },
  SAFETY_LOCKOUT_BLUE_FLAG: {
    primaryColor: 'electricBlue',
    strokeWidth: 6,
    glowBlur: 40,
    pattern: 'solid',
  },
}

/**
 * Get highlight configuration for a given error code, theme, role, and simulation state.
 * 
 * @param errorCode - The validation error code (e.g., 'FOUL_ZONE_VIOLATION')
 * @param theme - The active system theme variant
 * @param role - The entity role ('CAUSE' | 'VICTIM' | undefined for primary)
 * @param isSimulation - Whether this is a simulation ghost overlay
 * @returns HighlightConfig object with visual styling properties
 */
export function getHighlightConfig(
  errorCode: string,
  theme: ThemeVariant,
  role?: EntityRole,
  isSimulation?: boolean
): HighlightConfig {
  const baseConfig = errorConfigurations[errorCode] || {
    primaryColor: 'warningOrange',
    strokeWidth: 3,
    glowBlur: 15,
    pattern: 'solid',
  }

  const colors = themeColors[theme]
  const primaryColor = colors[baseConfig.primaryColor]
  const warningOrange = colors.warningOrange

  // Determine styling based on role
  let strokeColor: string
  let strokeWidth: number
  let fillColor: string | undefined
  let glowEnabled: boolean
  let glowColor: string | undefined
  let glowBlur: number
  let pattern: PatternType
  let opacity: number | undefined

  if (role === 'CAUSE') {
    // CAUSE: Heavy fill/glow with primary color
    strokeColor = primaryColor
    strokeWidth = baseConfig.strokeWidth + 2
    fillColor = primaryColor
    glowEnabled = true
    glowColor = primaryColor
    glowBlur = baseConfig.glowBlur + 10
    pattern = 'solid'
  } else if (role === 'VICTIM') {
    // VICTIM: Warning Orange dashed outline
    strokeColor = warningOrange
    strokeWidth = baseConfig.strokeWidth
    fillColor = undefined
    glowEnabled = false
    glowColor = undefined
    glowBlur = 0
    pattern = 'dashed'
  } else {
    // Primary: Standard highlight with primary color and low-opacity fill
    strokeColor = primaryColor
    strokeWidth = baseConfig.strokeWidth
    fillColor = primaryColor
    glowEnabled = true
    glowColor = primaryColor
    glowBlur = baseConfig.glowBlur
    pattern = baseConfig.pattern
  }

  // Apply simulation overlay styling
  if (isSimulation) {
    opacity = 0.4
    pattern = 'dashed'
    glowEnabled = false
  }

  return {
    strokeColor,
    strokeWidth,
    fillColor,
    glowEnabled,
    glowColor,
    glowBlur,
    pattern,
    opacity,
  }
}

/**
 * Get label override text for a given error code.
 * Returns undefined if no override is defined.
 * 
 * @param errorCode - The validation error code
 * @returns Label override string or undefined
 */
export function getLabelOverride(errorCode: string): string | undefined {
  const labelOverrides: Record<string, string> = {
    SAFETY_LOCKOUT_BLUE_FLAG: '🚩 BLUE FLAG',
    FOUL_ZONE_VIOLATION: '⚠️ FOUL ZONE',
    PATH_BLOCKED: '🚫 BLOCKED',
    CAPACITY_EXCEEDED: '📊 CAPACITY',
    HAZMAT_SEGREGATION_VIOLATION: '☢️ HAZMAT',
  }
  return labelOverrides[errorCode]
}

/**
 * Check if an error code is supported by the registry.
 * 
 * @param errorCode - The validation error code
 * @returns True if the error code has a configuration
 */
export function isErrorCodeSupported(errorCode: string): boolean {
  return errorCode in errorConfigurations
}

/**
 * Get all supported error codes.
 * 
 * @returns Array of supported error code strings
 */
export function getSupportedErrorCodes(): string[] {
  return Object.keys(errorConfigurations)
}
